import { useMemo, useState } from "react";
import { PaginationProps, Flex } from "antd";
import { getColumns } from "./utils/table";
import { ExpenseType } from "@/types/expenseType";
import {
  useGetExpenseDescriptionSummary,
  useGetExpenseDescriptionSummaryOptions,
} from "../../hooks/useReports";
import { DEFAULT_PAGINATION } from "@/constants/table";
import Title from "antd/es/typography/Title";
import { useLocation, useNavigate } from "react-router-dom";
import { ArrowLeftOutlined } from "@ant-design/icons";
import { SortCondition } from "@/components/table/hooks/useSort";
import Table, { FilterOptions } from "@/components/table";
import { FilterCondition } from "@/components/table/hooks/useFilter";

interface ExpenseGroupsProps {
  expenseType?: ExpenseType;
  startDate?: string;
  endDate?: string;
}

const ExpenseGroups = () => {
  const navigate = useNavigate();
  const [sort, setSort] = useState<SortCondition | undefined>({
    field: "total_amount",
    order: "desc",
  });
  const [filters, setFilters] = useState<FilterCondition[]>([]);
  const [pagination, setPagination] = useState<PaginationProps>();
  const stateData = useLocation().state as ExpenseGroupsProps | undefined;
  const { expenseType, startDate, endDate } = stateData || {};

  // Get expense description summary by type
  const { data, isFetching } = useGetExpenseDescriptionSummary({
    config: {
      params: {
        expense_type_id_eq: expenseType?.id,
        date_between:
          startDate && endDate ? `${startDate},${endDate}` : undefined,
        filters: filters.length > 0 ? filters : undefined,
        sort: sort ? [{ field: sort.field, order: sort.order }] : undefined,
        include_inactive: false,
        limit: pagination?.pageSize,
        page: pagination?.current,
        offset: ((pagination?.current || 1) - 1) * (pagination?.pageSize || 10),
      },
    },
    enabled: !!expenseType?.id,
  });

  // Get filter options
  const { data: options } = useGetExpenseDescriptionSummaryOptions({
    config: {
      params: {
        only: "description.id,description.name,unit.id,unit.name",
        expense_type_id_eq: expenseType?.id,
        date_between:
          startDate && endDate ? `${startDate},${endDate}` : undefined,
        include_inactive: false,
        limit: -1,
      },
    },
    enabled: !!expenseType?.id,
  });

  const filterOptions = useMemo(
    () => ({
      description_id: (options || []).reduce((acc, item) => {
        if (
          item.description &&
          !acc.some((option) => option.value === item?.description?.id)
        ) {
          acc.push({
            text: item.description.name,
            value: item.description.id,
          });
        }

        return acc;
      }, [] as FilterOptions[]),
      unit_id: (options || []).reduce((acc, item) => {
        if (
          item.unit &&
          !acc.some((option) => option.value === item?.unit?.id)
        ) {
          acc.push({
            text: item.unit.name,
            value: item.unit.id,
          });
        }
        return acc;
      }, [] as FilterOptions[]),
    }),
    [options]
  );

  // Redirect to reports if expense type is not found
  if (!expenseType) {
    navigate("/overview");
  }

  return (
    <>
      <Flex
        vertical={false}
        gap={16}
        style={{
          marginBottom: 24,
          alignContent: "center",
          alignItems: "center",
        }}
      >
        <ArrowLeftOutlined
          style={{
            cursor: "pointer",
            color: "#000000",
            fontSize: 18,
          }}
          onClick={() => navigate(-1)}
        />
        <Title level={2} style={{ textTransform: "capitalize", margin: 0 }}>
          {expenseType?.name.toLowerCase()}
        </Title>
      </Flex>

      <Table
        columns={(props) =>
          getColumns({
            ...props,
            filterOptions,
          })
        }
        onRow={(record) => ({
          style: {
            cursor: "pointer",
          },
          onClick: () => {
            navigate(`/overview/expense-groups/detail`, {
              state: {
                expenseType,
                description: record.description,
                startDate,
                endDate,
              },
            });
          },
        })}
        rowKey={(record) => record.description.id}
        dataSource={data?.data || []}
        loading={isFetching}
        pagination={{
          defaultPagination: {
            ...DEFAULT_PAGINATION,
            pageSize: 50,
            showSizeChanger: true,
          },
          dataPagination: data?.pagination,
          onChange: setPagination,
        }}
        scroll={{ x: "max-content" }}
        initialSort={sort}
        onSort={setSort}
        onFilter={setFilters}
      />
    </>
  );
};

export default ExpenseGroups;
